import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyInsurancePolicies } from 'src/components/insurance-policy/grid/EmptyInsurancePolicies'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  INSURANCE_POLICIES_GRID_COLUMNS_STORAGE_KEY,
  insurancePolicyPageSizeOptions,
} from 'src/constants/insurance-policy/constants'
import { InsurancePoliciesTableFields } from 'src/constants/insurance-policy/enums'
import { buildInsurancePolicyColumns } from 'src/constants/insurance-policy/insurancePolicyGrid'
import type { InsurancePolicy } from 'src/interfaces/insurancePolicy'
import { getInsuranceProducts } from 'src/selectors/insuranceProduct'
import { getInsurancePolicies, getInsurancePoliciesLoading } from 'src/selectors/insurancePolicy'
import {
  filterInsurancePolicies,
  withResolvedInsuranceProductsForPolicies,
} from 'src/utils/insurancePolicyList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
  canManage: boolean
  onOpenPolicy(policy: InsurancePolicy): void
  onEditPolicy(policy: InsurancePolicy): void
  onDeletePolicy(policy: InsurancePolicy): void
}

// The PERMANENT id, not `id`: an immutable mutation gives the same policy a new `id`, and a row
// id that changes under the grid resets its selection and focus.
const getRowId = (row: InsurancePolicy) => {
  return row.permanentId
}

// Scoped to the rows, not the grid: `GridTable` merges what it is given into its outer wrapper,
// so a bare `cursor-pointer` would also cover the empty space below the last row and the
// pagination footer — neither of which opens a policy.
const rowPointerStyles = { '& .MuiDataGrid-row': tw`cursor-pointer` }

// Name identifies the row and the actions column has no meaning when hidden; both also carry
// `hideable: false`, which is what removes them from the manage-columns panel. This guard
// additionally stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  InsurancePoliciesTableFields.name,
  InsurancePoliciesTableFields.actions,
]

const InsurancePoliciesTableComponent = ({
  searchQuery,
  canManage,
  onOpenPolicy,
  onEditPolicy,
  onDeletePolicy,
}: Props) => {
  const policies = useSelector(getInsurancePolicies)
  const isLoading = useSelector(getInsurancePoliciesLoading)
  const insuranceProductCatalog = useSelector(getInsuranceProducts)

  const { paginationModel, setPaginationModel } = useGridPagination(insurancePolicyPageSizeOptions)

  // The list endpoint's nested `insuranceProduct` is stripped by a backend DTO bug (ENG-750) —
  // backfill it from the catalog before the search below runs, so search-by-product-name works
  // and the "Products in policy" chips render names instead of raw UUIDs.
  const resolvedPolicies = useMemo(() => {
    return withResolvedInsuranceProductsForPolicies(policies, insuranceProductCatalog)
  }, [policies, insuranceProductCatalog])

  // A fresh array each render would reset the grid's column state, so the factory call is
  // memoized — and it depends only on the two stable handlers.
  const insurancePolicyColumns = useMemo(() => {
    return buildInsurancePolicyColumns(onEditPolicy, onDeletePolicy, canManage)
  }, [onEditPolicy, onDeletePolicy, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    insurancePolicyColumns,
    INSURANCE_POLICIES_GRID_COLUMNS_STORAGE_KEY,
  )

  // The client holds every policy for the customer (the endpoint takes no search param), so
  // filtering is here.
  const visiblePolicies = useMemo(() => {
    return filterInsurancePolicies(resolvedPolicies, searchQuery)
  }, [resolvedPolicies, searchQuery])

  const columnVisibilityModel = useMemo(() => {
    return insurancePolicyColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [insurancePolicyColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      insurancePolicyColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [insurancePolicyColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const handleRowClick = useCallback(
    (params: { row: InsurancePolicy }) => {
      onOpenPolicy(params.row)
    },
    [onOpenPolicy],
  )

  const hasSearchQuery = searchQuery.trim() !== ''

  // `GridTable` renders EmptyElement with no props, so the search-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyInsurancePolicies = () => {
      return <EmptyInsurancePolicies hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyInsurancePolicies
  }, [hasSearchQuery])

  return (
    <GridTable<InsurancePolicy>
      className={rowPointerStyles}
      rows={visiblePolicies}
      columns={insurancePolicyColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={insurancePolicyPageSizeOptions}
      onRowClick={handleRowClick}
      initialState={{
        // para-node's `findFiltered` is a bare `getMany()` with no ORDER BY, so this sort model
        // is the only thing making the order stable between loads.
        sorting: { sortModel: [{ field: InsurancePoliciesTableFields.name, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="insurance-policies-list-table"
      rowDataTestIdPrefix="insurance-policies-list"
    />
  )
}

export const InsurancePoliciesTable = memo(InsurancePoliciesTableComponent)
